// Dynamic Subscribe Block JavaScript
class DynamicSubscribeBlock {
  constructor(container) {
    this.container = container;
    this.sectionId = container.dataset.sectionId;
    this.init();
  }

  init() {
    this.bindEvents();
    this.handleURL();
  }

  bindEvents() {
    // Subscribe button click
    const subscribeTrigger = this.container.querySelector('[data-subscribe-trigger]');
    if (subscribeTrigger) {
      subscribeTrigger.addEventListener('click', () => this.showForm());
    }

    // Back button click
    const backTrigger = this.container.querySelector('[data-back-trigger]');
    if (backTrigger) {
      backTrigger.addEventListener('click', () => this.showMain());
    }

    // Form submission
    const subscriptionForm = this.container.querySelector('[data-subscription-form]');
    if (subscriptionForm) {
      subscriptionForm.addEventListener('submit', (e) => this.handleSubmit(e));
    }

    // Custom link handling
    const customLink = this.container.querySelector('[data-custom-link]');
    if (customLink) {
      customLink.addEventListener('click', (e) => this.handleCustomLink(e));
    }
  }

  handleURL() {
    // Check if URL has subscribe parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('subscribe') === 'true') {
      this.showForm();
    }
  }

  showForm() {
    const mainBlock = this.container.querySelector('[data-subscribe-block]');
    const formBlock = this.container.querySelector('[data-subscribe-form]');
    
    if (mainBlock && formBlock) {
      mainBlock.style.display = 'none';
      formBlock.classList.add('active', 'fade-in');
      
      // Update URL without page reload
      const url = new URL(window.location);
      url.searchParams.set('subscribe', 'true');
      window.history.pushState({}, '', url);
      
      // Focus on email input
      const emailInput = formBlock.querySelector('[data-email-input]');
      if (emailInput) {
        setTimeout(() => emailInput.focus(), 300);
      }
    }
  }

  showMain() {
    const mainBlock = this.container.querySelector('[data-subscribe-block]');
    const formBlock = this.container.querySelector('[data-subscribe-form]');
    
    if (mainBlock && formBlock) {
      formBlock.classList.remove('active', 'fade-in');
      setTimeout(() => {
        mainBlock.style.display = 'block';
      }, 300);
      
      // Update URL
      const url = new URL(window.location);
      url.searchParams.delete('subscribe');
      window.history.pushState({}, '', url);
    }
  }

  handleCustomLink(e) {
    e.preventDefault();
    const link = e.currentTarget;
    const href = link.getAttribute('href');
    
    // Add loading state
    const originalText = link.textContent;
    link.textContent = 'Redirecting...';
    link.style.pointerEvents = 'none';
    
    // Track click event
    this.trackEvent('custom_subscribe_click', { href });
    
    // Navigate after brief delay
    setTimeout(() => {
      window.location.href = href;
    }, 500);
  }

  async handleSubmit(e) {
    e.preventDefault();
    
    const form = e.target;
    const emailInput = form.querySelector('[data-email-input]');
    const submitButton = form.querySelector('[data-submit-button]');
    const email = emailInput.value.trim();
    
    // Validate email
    if (!this.validateEmail(email)) {
      this.showError(emailInput, 'Please enter a valid email address');
      return;
    }
    
    // Show loading state
    const originalText = submitButton.textContent;
    submitButton.textContent = 'Processing...';
    submitButton.disabled = true;
    submitButton.classList.add('processing');
    
    try {
      // Here you would typically send to your email service
      // For Shopify, you can use the customer registration or newsletter API
      await this.subscribeEmail(email);
      
      // Show success state
      this.showSuccess(email);
      
      // Track successful subscription
      this.trackEvent('subscription_success', { email });
      
    } catch (error) {
      console.error('Subscription error:', error);
      this.showError(emailInput, 'Something went wrong. Please try again.');
      submitButton.textContent = originalText;
      submitButton.disabled = false;
      submitButton.classList.remove('processing');
    }
  }

  validateEmail(email) {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  showError(input, message) {
    // Remove any existing error
    this.clearError(input);
    
    // Add error class
    input.classList.add('error');
    
    // Create error message element
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message';
    errorDiv.textContent = message;
    errorDiv.style.display = 'block';
    
    // Insert after input
    input.parentNode.insertBefore(errorDiv, input.nextSibling);
    
    // Focus on input
    input.focus();
  }

  clearError(input) {
    input.classList.remove('error');
    const errorMessage = input.nextElementSibling;
    if (errorMessage && errorMessage.classList.contains('error-message')) {
      errorMessage.remove();
    }
  }

  async subscribeEmail(email) {
    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    // In a real implementation, you would:
    // 1. Use Shopify's customer API: /admin/api/customers.json
    // 2. Or integrate with an email marketing service
    // 3. Or use Shopify's email marketing if available
    
    // For demo purposes, we'll simulate success
    return { success: true, email };
  }

  showSuccess(email) {
    const formBlock = this.container.querySelector('[data-subscribe-form]');
    
    if (!formBlock) return;
    
    formBlock.innerHTML = `
      <div class="success-state">
        <div class="success-icon">
          <i class="fas fa-check-circle"></i>
        </div>
        <h2 class="success-title">Subscription Confirmed!</h2>
        <p class="success-message">
          Thank you for subscribing! You'll now receive first-class access to Aarogyaapp news, expert tips, and exclusive member privileges.
        </p>
        <p class="success-message">
          A confirmation email has been sent to <strong>${email}</strong>.
        </p>
        <button type="button" class="return-home" data-return-home>
          Return to Home
        </button>
      </div>
    `;
    
    // Add event listener to return button
    const returnButton = formBlock.querySelector('[data-return-home]');
    if (returnButton) {
      returnButton.addEventListener('click', () => {
        this.showMain();
        // Reset form for next user
        setTimeout(() => this.resetForm(), 300);
      });
    }
  }

  resetForm() {
    const formBlock = this.container.querySelector('[data-subscribe-form]');
    if (formBlock) {
      // Re-render the form (Shopify will handle this on next load)
      // In a real implementation, you might want to use AJAX to reload just the form
      location.reload();
    }
  }

  trackEvent(eventName, data) {
    // Track events for analytics
    if (typeof gtag !== 'undefined') {
      gtag('event', eventName, data);
    }
    
    if (typeof fbq !== 'undefined') {
      fbq('track', eventName, data);
    }
    
    // Log to console for debugging
    console.log(`Event: ${eventName}`, data);
  }
}

// Initialize when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  const subscribeBlocks = document.querySelectorAll('[data-section-type="dynamic-subscribe-block"]');
  
  subscribeBlocks.forEach(block => {
    new DynamicSubscribeBlock(block);
  });
});

// Handle browser back/forward buttons
window.addEventListener('popstate', () => {
  const urlParams = new URLSearchParams(window.location.search);
  const subscribeBlocks = document.querySelectorAll('[data-section-type="dynamic-subscribe-block"]');
  
  subscribeBlocks.forEach(block => {
    const instance = new DynamicSubscribeBlock(block);
    if (urlParams.get('subscribe') === 'true') {
      instance.showForm();
    } else {
      instance.showMain();
    }
  });
});